// Pure EVM call encoding for sponsored transfers. Browser-safe (viem only) — no
// account-abstraction SDK here; the API's relayer-evm builds/submits the op.

import { encodeFunctionData, erc20Abi, getAddress, parseUnits, type Address, type Hex } from 'viem';
import { requireTokenInfo, type ContractChain, type TokenSymbol } from '@incpt/shared';

/** A single UserOperation call (target/data/value), as Account Kit expects. */
export interface Call {
  target: Address;
  data: Hex;
  value: bigint;
}

export function encodeErc20Transfer(tokenAddress: string, to: string, amountUnits: bigint): Call {
  return {
    target: getAddress(tokenAddress),
    value: 0n,
    data: encodeFunctionData({
      abi: erc20Abi,
      functionName: 'transfer',
      args: [getAddress(to), amountUnits],
    }),
  };
}

/**
 * EVM batch for one sponsored op: send `amount` of the token to `to`, plus the
 * flat `feeUsd` fee (same stablecoin, ~$1 each) to `treasury`. EVM only — Tron
 * has no atomic batch and handles the fee as a second TRC-20 transfer.
 */
export function encodeTransferBatch(opts: {
  /** Contract-token chains only — native coins never batch a treasury fee leg. */
  chain: ContractChain;
  token: TokenSymbol;
  to: string;
  amount: string;
  treasury: string;
  feeUsd: number;
}): Call[] {
  const { address, decimals } = requireTokenInfo(opts.chain, opts.token);
  const amountUnits = parseUnits(opts.amount, decimals);
  const calls = [encodeErc20Transfer(address, opts.to, amountUnits)];
  const feeUnits = parseUnits(opts.feeUsd.toString(), decimals);
  if (feeUnits > 0n && opts.treasury) {
    calls.push(encodeErc20Transfer(address, opts.treasury, feeUnits));
  }
  return calls;
}
